import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import TimelineIcon from '@mui/icons-material/Timeline'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import WTypography from 'Components/whiteTypography'
import ProfilForm from './ProfilForm'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== ''

function ProfilUtilisateur({ oldData, updateReports, openFromUrl = false, onUrlOpenConsumed = null }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  // LAP-733 (GE022): profileFields built via useMemo so labels re-evaluate on locale change
  const profileFields = useMemo(() => [
    { name: 'User_Job', label: t('professionnelle.fieldCurrentJob') },
    { name: 'User_JobYear', label: t('professionnelle.fieldExperienceYears'), type: 'number' },
    { name: 'future_job1', label: t('professionnelle.fieldFutureJob1') },
    { name: 'future_job2', label: t('professionnelle.fieldFutureJob2') },
    { name: 'future_job3', label: t('professionnelle.fieldFutureJob3') },
  ], [t])
  const [open, setOpen] = useState(false)
  const [inputField, setInputField] = useState([])

  const currentJob = oldData.User_Job
  const experienceYears = oldData.User_JobYear
  const futureJobs = [oldData.future_job1, oldData.future_job2, oldData.future_job3].filter(hasValue)
  const completedCount = profileFields.filter((field) => hasValue(oldData[field.name])).length
  const isProfileEmpty = completedCount === 0

  const handleForm = (fields) => {
    setInputField(fields.map((field) => ({ ...field, value: oldData[field.name] })))
    setOpen(true)
  }

  const handleFullProfileForm = () => {
    handleForm(profileFields)
  }

  useEffect(() => {
    if (openFromUrl) {
      handleFullProfileForm()
      if (onUrlOpenConsumed) onUrlOpenConsumed()
    }
  }, [openFromUrl, onUrlOpenConsumed])

  return (
    <>
      {open && (
        <ProfilForm
          updateReports={updateReports}
          setOpen={setOpen}
          inputField={inputField}
        />
      )}
      <Card
        className="dashboard_card__root1"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          color: 'white',
          overflow: 'hidden',
        }}
      >
        <Box className="dashboard_card__header1 no-drag">
          <Box>
            <WTypography component="span" variant="h6">
              {t('professionnelle.profileTitle') /* LAP-733 (GE022) */}
            </WTypography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, mt: 0.5 }}>
              {t('professionnelle.profileInfoCompleted', { completed: completedCount, total: profileFields.length }) /* LAP-733 (GE022) */}
            </Typography>
          </Box>
          <Button
            className="no-drag"
            onClick={handleFullProfileForm}
            startIcon={isProfileEmpty ? <AddIcon /> : <EditIcon />}
            sx={{
              color: '#c1daf1',
              fontWeight: 800,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            {isProfileEmpty ? t('professionnelle.btnComplete') : t('professionnelle.btnEdit') /* LAP-733 (GE022) */}
          </Button>
        </Box>

        {isProfileEmpty ? (
          <Stack
            className="no-drag"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            spacing={1.5}
            sx={{ flex: 1, minHeight: 300, px: 2 }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.16)',
              }}
            >
              <WorkOutlineIcon sx={{ color: '#c1daf1', fontSize: 38 }} />
            </Box>
            <Typography sx={{ fontWeight: 850, fontSize: 20 }}>{t('professionnelle.emptyTitle') /* LAP-733 (GE022) */}</Typography>
            <Typography sx={{ maxWidth: 360, color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
              {t('professionnelle.emptyDescription') /* LAP-733 (GE022) */}
            </Typography>
            <Button
              onClick={handleFullProfileForm}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                bgcolor: '#1b75d0',
                borderRadius: 999,
                px: 2.6,
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': { bgcolor: '#145da8' },
              }}
            >
              {t('professionnelle.btnCompleteProfile') /* LAP-733 (GE022) */}
            </Button>
          </Stack>
        ) : (
          <Stack className="no-drag" spacing={2.5} sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <WorkOutlineIcon sx={{ color: '#c1daf1', fontSize: 20 }} />
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 13 }}>
                      {t('professionnelle.fieldCurrentJob') /* LAP-733 (GE022) */}
                    </Typography>
                  </Stack>
                  <EditIcon
                    onClick={() => handleForm([profileFields[0]])}
                    sx={{ color: 'white', cursor: 'pointer', fontSize: 20 }}
                  />
                </Stack>
                <Typography sx={{ fontWeight: 850, fontSize: 19, mt: 1, lineHeight: 1.2 }}>
                  {hasValue(currentJob) ? currentJob : t('professionnelle.toFill') /* LAP-733 (GE022) */}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: { xs: '100%', sm: 150 },
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <TimelineIcon sx={{ color: '#c1daf1', fontSize: 20 }} />
                  <EditIcon
                    onClick={() => handleForm([profileFields[1]])}
                    sx={{ color: 'white', cursor: 'pointer', fontSize: 20 }}
                  />
                </Stack>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 13, mt: 1 }}>
                  {t('professionnelle.experience') /* LAP-733 (GE022) */}
                </Typography>
                <Typography sx={{ fontWeight: 850, fontSize: 19, lineHeight: 1.2 }}>
                  {hasValue(experienceYears) ? t('professionnelle.years', { years: experienceYears }) : t('professionnelle.toFill') /* LAP-733 (GE022) */}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{t('professionnelle.desiredJobs') /* LAP-733 (GE022) */}</Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 13 }}>
                    {t('professionnelle.desiredJobsSubtitle') /* LAP-733 (GE022) */}
                  </Typography>
                </Box>
                <EditIcon
                  onClick={() => handleForm(profileFields.slice(2))}
                  sx={{ color: 'white', cursor: 'pointer' }}
                />
              </Stack>

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {futureJobs.length ? (
                  futureJobs.map((job, index) => (
                    <Chip
                      key={`${job}-${index}`}
                      label={job}
                      sx={{
                        bgcolor: 'rgba(193, 218, 241, 0.14)',
                        color: '#ffffff',
                        border: '1px solid rgba(193, 218, 241, 0.28)',
                        fontWeight: 700,
                      }}
                    />
                  ))
                ) : (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.72)', fontSize: 14 }}>
                    {t('professionnelle.addThreeTargetJobs') /* LAP-733 (GE022) */}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Card>
    </>
  )
}

export default ProfilUtilisateur
